/**
 * Phase 3.6 — FailureModeBreakdownPanel
 *
 * Donut + legend of the recommended candidate's failure-mode
 * distribution. Pulls the per-mode weight dictionary once on mount
 * from /api/optimisation/failure-mode-weights and caches it in local
 * state — the backend already merges tenant + default rows.
 *
 * v1 caveat banner appears when ``failure_mode_distribution_source ===
 * "projection_from_dominant"`` so the operator knows the breakdown is
 * a coarse projection and a richer histogram is planned.
 */
import { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import optimisation from "@/api/optimisation";
import { formatApiError } from "@/lib/formatApiError";

// Cream-teal for "none" (success bucket); graded amber→red for failure
// modes ordered by weight descending.
const NONE_COLOR = "var(--ux-info)";
const FAILURE_PALETTE = [
    "var(--ux-danger)",
    "#e8775d",
    "#e8975d",
    "var(--ux-warning)",
    "#c9b86a",
    "#a39458",
];

function modeLabel(m) {
    if (!m) return "—";
    if (m === "none") return "Successful intercept";
    return m.replace(/_/g, " ");
}

function fmtPct(p) {
    if (typeof p !== "number" || Number.isNaN(p)) return "—";
    return `${(p * 100).toFixed(1)} %`;
}

export default function FailureModeBreakdownPanel({ diff }) {
    const rec = diff?.recommended;
    const dist = rec?.failure_mode_distribution;
    const source = rec?.failure_mode_distribution_source;

    const [weightMap, setWeightMap] = useState(null);
    const [weightErr, setWeightErr] = useState(null);

    useEffect(() => {
        if (!dist || Object.keys(dist).length === 0) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const resp = await optimisation.listFailureModeWeights();
                if (cancelled) return;
                // Backend returns { tenant_id, rows: [{failure_mode, weight}], defaults }
                const flat = {};
                (resp?.rows || []).forEach((r) => {
                    if (r?.failure_mode) flat[r.failure_mode] = r.weight;
                });
                Object.entries(resp?.defaults || {}).forEach(([k, v]) => {
                    if (!(k in flat)) flat[k] = v;
                });
                setWeightMap(flat);
            } catch (e) {
                if (!cancelled) {
                    setWeightErr(
                        formatApiError(e, "weights unavailable"),
                    );
                    setWeightMap({});
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dist]);

    const sortedRows = useMemo(() => {
        if (!dist) return [];
        // Order: none first; failures sorted by weight DESC.
        const failureKeys = Object.keys(dist).filter((k) => k !== "none");
        failureKeys.sort((a, b) => {
            const wa = weightMap?.[a] ?? 0.5;
            const wb = weightMap?.[b] ?? 0.5;
            return wb - wa;
        });
        const ordered = [];
        if ("none" in dist) ordered.push("none");
        ordered.push(...failureKeys);
        return ordered.map((mode, idx) => ({
            mode,
            prob: dist[mode],
            color:
                mode === "none"
                    ? NONE_COLOR
                    : FAILURE_PALETTE[
                          (idx - (("none" in dist) ? 1 : 0)) % FAILURE_PALETTE.length
                      ],
            weight: mode === "none" ? null : weightMap?.[mode] ?? null,
        }));
    }, [dist, weightMap]);

    if (!dist || Object.keys(dist).length === 0) return null;

    const chartData = sortedRows
        .filter((r) => typeof r.prob === "number" && r.prob > 0)
        .map((r) => ({ name: modeLabel(r.mode), value: r.prob, color: r.color }));

    const showCaveat = source === "projection_from_dominant";

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-3"
            data-testid="failure-mode-breakdown-panel"
        >
            <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-ux-accent" />
                <h3 className="text-[13px] font-medium text-ux-text-1">
                    Failure-mode breakdown
                </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                    style={{ width: "100%", height: 160 }}
                    data-testid="failure-mode-donut"
                >
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={36}
                                outerRadius={64}
                                paddingAngle={1.5}
                                dataKey="value"
                                isAnimationActive={false}
                            >
                                {chartData.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(v) => [fmtPct(v), ""]}
                                contentStyle={{
                                    background: "var(--ux-bg-1)",
                                    border: "1px solid var(--ux-border-1)",
                                    fontSize: "11px",
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="sm:col-span-2 space-y-1">
                    <ul
                        className="space-y-1"
                        data-testid="failure-mode-legend"
                    >
                        {sortedRows.map((r) => (
                            <li
                                key={r.mode}
                                className="flex items-baseline gap-2 text-[12px]"
                                data-testid={`failure-mode-row-${r.mode}`}
                            >
                                <span
                                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                                    style={{ background: r.color }}
                                />
                                <span className="text-ux-text-2 flex-1 capitalize">
                                    {modeLabel(r.mode)}
                                </span>
                                <span className="font-mono tabular-nums text-ux-text-1 w-16 text-right">
                                    {fmtPct(r.prob)}
                                </span>
                                {r.weight !== null && (
                                    <span
                                        className="text-[10px] font-mono text-ux-text-3 w-14 text-right"
                                        title="Per-mode penalty weight (admin-tunable)"
                                    >
                                        w={typeof r.weight === "number" ? r.weight.toFixed(2) : "—"}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                    {weightErr && (
                        <p className="text-[10px] text-ux-text-3 italic">
                            (weights unavailable: {weightErr})
                        </p>
                    )}
                </div>
            </div>

            {showCaveat && (
                <div
                    className="flex items-start gap-2 rounded border border-ux-warning/40 bg-ux-warning-soft/30 p-2.5 text-[11px] text-ux-warning"
                    data-testid="failure-mode-projection-caveat"
                >
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                        Failure-mode penalty uses a coarse two-bucket
                        projection from the dominant failure mode; a richer
                        per-seed histogram is planned for the next engine
                        revision.
                    </span>
                </div>
            )}
        </div>
    );
}
